import React, { useEffect, useState } from 'react';
import { message, Modal } from 'antd';
import styled from 'styled-components';
import {
    useGetIngestionSourceQuery,
    useCancelIngestionExecutionRequestMutation,
    useRollbackIngestionMutation,
} from '../../../../graphql/ingestion.generated';
import { Message } from '../../../shared/Message';
import { ExecutionDetailsModal } from './ExecutionRequestDetailsModal';
import IngestionExecutionTable from './IngestionExecutionTable';
import { ExecutionRequest } from '../../../../types.generated';
import { ROLLING_BACK, RUNNING } from '../utils';
import useRefreshIngestionData from './useRefreshIngestionData';
import { ComponentSpinner } from '../../../shared/ComponentSpinner';
import { useTranslation } from '../../../../i18n/useTranslation';

const ListContainer = styled.div`
    margin-left: 28px;
`;

export function isExecutionRequestActive(executionRequest: ExecutionRequest) {
    return executionRequest.result?.status === RUNNING || executionRequest.result?.status === ROLLING_BACK;
}

type Props = {
    urn: string;
    isExpanded: boolean;
    lastRefresh: number;
    onRefresh: () => void;
    canCancel?: boolean;
};

export const IngestionSourceExecutionList = ({ urn, isExpanded, lastRefresh, onRefresh, canCancel = true }: Props) => {
    const { t } = useTranslation();
    const [focusExecutionUrn, setFocusExecutionUrn] = useState<undefined | string>(undefined);
    const [isLoading, setIsLoading] = useState(false);

    const start = 0;
    const count = 10; // Load 10 items at a time.

    const { loading, data, error, refetch } = useGetIngestionSourceQuery({
        variables: {
            urn,
            runStart: start,
            runCount: count,
        },
    });

    const sourceName = data?.ingestionSource?.name;

    function hasActiveExecution() {
        return !!data?.ingestionSource?.executions?.executionRequests.find((request) =>
            isExecutionRequestActive(request as ExecutionRequest),
        );
    }
    useRefreshIngestionData(refetch, hasActiveExecution);

    const [cancelExecutionRequestMutation] = useCancelIngestionExecutionRequestMutation();
    const [rollbackIngestion] = useRollbackIngestionMutation();

    useEffect(() => {
        if (isExpanded) {
            refetch();
        }
    }, [lastRefresh, isExpanded, refetch]);

    const handleViewDetails = (focusUrn: string) => {
        setFocusExecutionUrn(focusUrn);
    };

    const onCancelExecutionRequest = (executionUrn: string) => {
        cancelExecutionRequestMutation({
            variables: {
                input: {
                    ingestionSourceUrn: urn,
                    executionRequestUrn: executionUrn,
                },
            },
        })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t(`Failed to cancel execution!: {{message}}`, { message: e.message || '' }),
                    duration: 3,
                });
            })
            .finally(() => {
                message.success({
                    content: t('Successfully submitted cancellation request!'),
                    duration: 3,
                });
                // Refresh once a job was cancelled.
                setTimeout(() => onRefresh(), 2000);
            });
    };

    const handleCancelExecution = (executionUrn: string) => {
        Modal.confirm({
            centered: true,
            title: t('Confirm Cancel'),
            content: t('CANCEL_EXECUTION_CONFIRMATION'),
            onOk() {
                onCancelExecutionRequest(executionUrn);
            },
            onCancel() {},
            okText: t('Cancel'),
            cancelText: t('Close'),
            maskClosable: true,
            closable: true,
        });
    };

    function handleRollbackExecution(runId: string) {
        Modal.confirm({
            title: t('Confirm Rollback'),
            content: (
                <div>
                    {t('ROLLBACK_EXECUTION_CONFIRMATION')}
                    <br />
                    <br />
                    {t('Are you sure you want to continue?')}
                </div>
            ),
            onOk() {
                setIsLoading(true);

                message.loading(t('Requesting rollback'));
                rollbackIngestion({ variables: { input: { runId } } })
                    .then(() => {
                        setTimeout(() => {
                            message.destroy();
                            refetch();
                            onRefresh();
                            message.success(t('Successfully requested ingestion rollback'));
                            setIsLoading(false);
                        }, 2000);
                    })
                    .catch(() => {
                        message.error(t('Error requesting ingestion rollback'));
                        setIsLoading(false);
                    });
            },
            onCancel() {},
            okText: t('Rollback'),
            cancelText: t('Close'),
            maskClosable: true,
            closable: true,
            centered: true,
        });
    }

    const executionRequests = (data?.ingestionSource?.executions?.executionRequests as ExecutionRequest[]) || [];

    return (
        <ListContainer>
            {!data && loading && <ComponentSpinner />}
            {error && (
                <Message
                    type="error"
                    content={t('Failed to load ingestion executions! An unexpected error occurred.')}
                />
            )}
            <IngestionExecutionTable
                executionRequests={executionRequests}
                setFocusExecutionUrn={setFocusExecutionUrn}
                handleCancelExecution={handleCancelExecution}
                handleViewDetails={handleViewDetails}
                handleRollbackExecution={handleRollbackExecution}
                isLoading={isLoading}
                canCancel={canCancel}
            />
            {focusExecutionUrn && (
                <ExecutionDetailsModal
                    urn={focusExecutionUrn}
                    name={sourceName}
                    visible={focusExecutionUrn !== undefined}
                    onClose={() => setFocusExecutionUrn(undefined)}
                />
            )}
        </ListContainer>
    );
};
